import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import {BetaFeature, ChatMessageTypeEnum, OrderRiderActionEnum} from 'app/models';
import {DeliveryOrderService, XpressUserService, AppDataService, XpressPaymentTransactionService} from 'app/services';
import { Utils } from 'app/utils';
import moment from 'moment';
import {PaymentProcessingService} from 'app/services/payment-processing.service';
import {jsonHelpUsage} from '@angular/cli/src/command-builder/utilities/json-help';

enum TransactionDetailsTab {
    Order = 1,
    Driver = 2,
    Merchant = 3,
    Customer = 4,
    Messages = 5,
    Timeline = 6
}

@Component({
    selector: 'home-transaction-details',
    templateUrl: './home-transaction-details.component.html',
    styleUrls: ['./home-transaction-details.component.scss'],
    providers: [MessageService]
})
export class HomeTransactionDetailsComponent implements OnInit {

    @Input()
    public showTransactionsSidebar: boolean = false;

    @Input()
    public onHide: () => void = () => { };

    public transactionId!: number;
    @Input()
    set _transactionId(value: number) {
        this.transactionId = value;
        if (this.transactionId > 0) {
            this.getTransaction();
            this.getOrderDetail();
        }
    }

    @Output()
    updateTransactionsEmit = new EventEmitter<boolean>();

    transaction: any = {};

    loading: boolean = false;
    hasAccessToRefund: boolean = false;
    visibleDriverDetail: boolean = false;
    visibleCustomerDetail: boolean = false;
    chatMessageType = ChatMessageTypeEnum;
    transactionDetailsTab = TransactionDetailsTab;
    activeTab: TransactionDetailsTab = TransactionDetailsTab.Order;

    customerStats: any;
    userAnalyticsLoading: boolean = false;
    orderStats: any = {};

    isRefundButtonDisabled: boolean = false;
    isRefundApplyButtonDisabled: boolean = false;
    showDialogRefundTransaction: boolean = false;
    explanationForRefund: string | null = null;
    showDetailOrderSideBar: boolean = false;
    orderId: number = 0;

    map!: google.maps.Map;

    refundReasons = [
        {
            id: 0,
            Name: 'None'
        },
        {
            id: 1,
            Name: 'Waited too long for the driver'
        },
        {
            id: 2,
            Name: 'Driver not moving'
        },
        {
            id: 3,
            Name: 'Driver said he arrived but hasn’t'
        },
        {
            id: 4,
            Name: 'Entered wrong address'
        },
        {
            id: 5,
            Name: 'Driver asked to cancel'
        },
        {
            id: 6,
            Name: 'Unsatisfactory service'
        },
        {
            id: 7,
            Name: 'Overcharged'
        },
        {
            id: 8,
            Name: 'Double charged'
        },
        {
            id: 9,
            Name: 'Incorrect billing'
        },
        {
            id: 10,
            Name: 'Trip not as described'
        }
    ];
    selectedRefundReason: { id: number; Name: string } | null = this.refundReasons[0];
    refundTypes = [
        {
            id: 0,
            Name: 'None'
        },
        {
            id: 1,
            Name: 'Refund to client\'s Xpay wallet'
        },
        {
            id: 2,
            Name: 'Void (Valid until the end of the day)'
        },
        {
            id: 3,
            Name: 'Refund at the customer\'s direct destination'
        },
    ];
    selectedRefundType: { id: number; Name: string } | null = this.refundTypes[0];

    menuItems: MenuItem[] = [
        {
            label: 'Order Details',
            id: `${TransactionDetailsTab.Order}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Driver Detail',
            id: `${TransactionDetailsTab.Driver}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Customer Detail',
            id: `${TransactionDetailsTab.Customer}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Merchant Detail',
            id: `${TransactionDetailsTab.Merchant}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Messages',
            id: `${TransactionDetailsTab.Messages}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        },
        {
            label: 'Timeline',
            id: `${TransactionDetailsTab.Timeline}`,
            command: (e: any) => this.handleTabChange(+e.item.id)
        }
    ];

    constructor(
        public appData: AppDataService,
        private messageService: MessageService,
        private xpressUserService: XpressUserService,
        private deliveryOrderService: DeliveryOrderService,
        private paymentProcessingService: PaymentProcessingService,
        private xpressPaymentTransactionService: XpressPaymentTransactionService,
    ) {
        this.handleCustomerDetailPopupHide = this.handleCustomerDetailPopupHide.bind(this);
        this.hasAccessToRefund = this.appData.betaFeaturesIsExist([BetaFeature.ADJUST_WALLET]);
    }

    ngOnInit(): void {
    }

    handleTabChange(tab: TransactionDetailsTab) {
        this.activeTab = tab;
        this.callMethods();
    }
    callMethods() {
        if (this.activeTab === TransactionDetailsTab.Customer && !this.customerStats && this.transaction.Customer) {
            this.getUserAnalytics();
        }
    }

    getUserAnalytics() {
        if (!this.orderStats.XpressDeliveryOrderId) {
            return;
        }
        const dto = {
            XpressUserId: this.orderStats.Customer.Id
        };
        this.userAnalyticsLoading = true;

        this.xpressUserService.getUserAnalytics(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.userAnalyticsLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.customerStats = data;
                this.userAnalyticsLoading = false;
            })
            .catch(err => {
                this.userAnalyticsLoading = false;
                throw err;
            });
    }

    getOrderDetail() {
        const dto = {
            TransactionId: this.transactionId
        };
        this.deliveryOrderService.getOrderDetailsByTransactionId(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.orderStats = data;
                this.orderStats.CreatedAt = Utils.utcToLocalDate(this.orderStats.CreatedAt);
                this.orderStats.AcceptedTime = (this.orderStats.AcceptedAt) ? moment(Utils.utcToLocalDate(this.orderStats.AcceptedAt)).format('hh:mm') : '';
                this.orderStats.PackingStartedAt = this.orderStats.PackingStartedAt ? Utils.utcToLocalDate(this.orderStats.PackingStartedAt) : '';
                this.orderStats.ReadyToDispatchAt = this.orderStats.ReadyToDispatchAt ? Utils.utcToLocalDate(this.orderStats.ReadyToDispatchAt) : '';
                this.orderStats.DispatchDateTime = this.orderStats.DispatchAt ? Utils.utcToLocalDate(this.orderStats.DispatchAt) : '';
                this.orderStats.DispatchTime = this.orderStats.DispatchAt ? moment(Utils.utcToLocalDate(this.orderStats.DispatchAt)).format('hh:mm') : '';
                this.orderStats.DeliveredDateTime = this.orderStats.DeliveredAt ? Utils.utcToLocalDate(this.orderStats.DeliveredAt) : '';
                this.orderStats.DeliveredTime = this.orderStats.DeliveredAt ? moment(Utils.utcToLocalDate(this.orderStats.DeliveredAt)).format('hh:mm') : '';
                this.orderStats.CompletedAt = this.orderStats.CompletedAt ? Utils.utcToLocalDate(this.orderStats.CompletedAt) : '';
                this.orderStats.CompletedTime = (this.orderStats.CompletedAt) ? moment(this.orderStats.CompletedAt).format('hh:mm') : '';
                this.orderStats.CanceledAt = this.orderStats.CanceledAt ? Utils.utcToLocalDate(this.orderStats.CanceledAt) : '';
                this.orderStats.RiderAssignedDateTime = (this.orderStats.RiderAssignedAt) ? Utils.utcToLocalDate(this.orderStats.RiderAssignedAt) : '';
                this.orderStats.RiderAssignedTime = (this.orderStats.RiderAssignedAt) ?
                    moment(Utils.utcToLocalDate(this.orderStats.RiderAssignedAt)).format('hh:mm') : '';
                this.orderStats.ArrivedToPickupDateTime = (this.orderStats.ArrivedToPickupAt) ?
                    Utils.utcToLocalDate(this.orderStats.ArrivedToPickupAt) : '';
                this.orderStats.ArrivedToPickupTime = (this.orderStats.ArrivedToPickupAt) ?
                    moment(Utils.utcToLocalDate(this.orderStats.ArrivedToPickupAt)).format('hh:mm') : '';
                if (this.orderStats.RiderActions) {
                    this.orderStats.RiderActions.forEach((item: any) => {
                        item.CreatedDateTime = (item.CreatedAt) ? Utils.utcToLocalDate(item.CreatedAt) : '';
                        item.CreatedTime = (item.CreatedAt) ? moment(Utils.utcToLocalDate(item.CreatedAt)).format('hh:mm') : '';
                    });
                    this.orderStats.RiderDropoffActions = this.orderStats.RiderActions
                        .filter((f: any) => (f.OrderRiderAction === OrderRiderActionEnum.ArrivedToDrop || f.OrderRiderAction === OrderRiderActionEnum.Completed));
                }
                if (this.orderStats.Messages) {
                    this.orderStats.Messages.map((item: any) => {
                        item.CreatedAt = Utils.utcToLocaleString(item.CreatedAt);
                        return item;
                    });
                }
                if (this.orderStats.LokalOrderId) {
                    this.googleMap(this.orderStats.RouteInfo.RoutingResultGoogle);
                }
                this.getUserAnalytics();
            });

    }

    getTransaction() {
        const dto = {
            TransactionId: this.transactionId
        };
        this.loading = true;
        this.xpressPaymentTransactionService.getTransactionByIdAdmin(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.loading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.transaction = data;
                this.transaction.CreatedAtTime = moment(Utils.utcToLocalDate(data.TransactionCreatedAt)).format('hh:mm:ss');
                this.transaction.CreatedAtDate = moment(data.TransactionCreatedAt).format('MMM Do YY');
                this.transaction.DeliveryCreatedTime = moment(Utils.utcToLocalDate(data.DeliveryCreatedAt)).format('hh:mm:ss');
                this.transaction.DeliveryCompletedTime = moment(Utils.utcToLocalDate(data.DeliveryCompletedAt)).format('hh:mm:ss');
                this.transaction.DeliveryCompletedAt = moment(data.DeliveryCompletedAt).format('MMM Do YY');
                this.transaction.AcceptedTime = data.AcceptedAt ? moment(Utils.utcToLocalDate(data.AcceptedAt)).format('hh:mm:ss') : null;
                this.transaction.PackingStartedTime = data.PackingStartedAt ? moment(Utils.utcToLocalDate(data.PackingStartedAt)).format('hh:mm:ss') : null;
                this.transaction.ReadyToDispatchTime = data.ReadyToDispatchAt ? moment(Utils.utcToLocalDate(data.ReadyToDispatchAt)).format('hh:mm:ss') : null;
                this.transaction.DispatchTime = data.DispatchAt ? moment(Utils.utcToLocalDate(data.DispatchAt)).format('hh:mm:ss') : null;
                this.transaction.DeliveredTime = data.DeliveredAt ? moment(Utils.utcToLocalDate(data.DeliveredAt)).format('hh:mm:ss') : null;
                this.loading = false;
            })
            .catch(err => {
                this.loading = false;
                throw err;
            });
    }

    googleMap(routingResultGoogle: any) {
        const origin = routingResultGoogle.routes[0].bounds.northeast.lat;
        const destination = routingResultGoogle.routes[0].bounds.southwest.lng;

        const directionsService = new google.maps.DirectionsService();
        const directionsRenderer = new google.maps.DirectionsRenderer();
        const centerMap = { lat: origin, lng: destination };
        const googleMap = new google.maps.Map(document.getElementById('map') as HTMLElement, {
            zoom: 12,
            center: centerMap,
        });
        const waypts = [];
        const legs = routingResultGoogle.routes[0].legs;

        legs.shift();
        legs.pop();

        for (let i = 0; i < legs.length - 1; i++) {
            waypts.push({
                location: new google.maps.LatLng(legs[i].end_location.lat, legs[i].end_location.lng),
                stopover: true,
            });
        }

        directionsService
            .route({
                origin: routingResultGoogle.origin,
                destination: routingResultGoogle.destination,
                waypoints: waypts,
                optimizeWaypoints: true,
                travelMode: google.maps.TravelMode.DRIVING,
            })
            .then((response) => {
                directionsRenderer.setDirections(response);

                directionsRenderer.setMap(googleMap);
            })
            .catch((e) => console.log(e));
    }

    changedVisibleDriverDetailEmit(value: any) {
        this.visibleDriverDetail = value;
    }
    handleCustomerDetailPopupHide() {
        this.visibleCustomerDetail = false;
    }

    showPopupRefundTransaction() {
        this.showDialogRefundTransaction = true;
    }

    refundTransactionCancel() {
        this.loading = false;
        this.showDialogRefundTransaction = false;
    }

    refundTransactionApply() {
        this.loading = true;
        this.isRefundButtonDisabled = true;
        this.isRefundApplyButtonDisabled = true;
        const dto = {
            TransactionId: this.transactionId,
            RefundReason: this.selectedRefundReason?.Name,
            RefundType: this.selectedRefundType?.id
        };
        this.paymentProcessingService.refundTransaction(dto, this.messageService)
            .then(data => {
                this.loading = false;
                this.showDialogRefundTransaction = false;
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.updateTransactions();
            })
            .catch(err => {
                this.loading = false;
                this.showDialogRefundTransaction = false;
                throw err;
            });
    }

    onAntiFraudActionCompleted(): void {
        this.getTransaction();
        this.updateTransactionsEmit.emit(true);
    }

    changedVisibleCustomerInfoEmit(value: any) {
        this.visibleCustomerDetail = value;
    }

    updateTransactions() {
        this.updateTransactionsEmit.emit(true);
    }

    openActiveOrderDetail(orderId: any) {
        this.orderId = orderId ? orderId : 0;
        if (this.orderId !== 0) {
            this.showDetailOrderSideBar = !this.showDetailOrderSideBar;
        }
    }
    changedShowDetailOrderSideBarEmit(value: any) {
        this.showDetailOrderSideBar = value;
    }

    cancel() {
        this.updateTransactionsEmit.emit(false);
        this.onHide();
    }
}
